/**
 * This is a table
 *
 * @parent anybody
 * @stateful
 *
 * IMPORTANT:
 * When there is a "nesting" tr, we use tbody as row, instead of tr
 * and data structure will be different when we use tbody as row.
 * This might sound a little confusing, but here is the explanation.
 *
 * When tr is a row, we use "traditional" structure, where data looks like this:
 * [ {}, {}, {}, {} ]
 *
 * where {} reprensents an individual item, within an array.
 *
 * BUT when this.props.useTbodyAsRow == true, we expect data to be in format of:
 * 		[ [ {}, {} ], [ {}, {} ], [ {}, {} ] ]
 * where each [] represents tbodies and {} within it represents trs.
 *
 * WHY WE ARE DOING THIS:
 * Sometimes we need nested rows (rows within rows) and this is
 * the best way to represent that structure. The best example would be
 * the table in Student page.
 *
 *
 */
import React from 'react'
import classnames from 'classnames'
import PropTypes from 'prop-types'
import forOwn from "lodash/forOwn"
import TableSetting from './TableSetting'
import "./table.scss"
import Pagination from 'react-js-pagination'
import Loading from 'components/UI/Loading'

const USE_PAGINATION = true;
const PAGE_RANGE_DISPLAYED = 5;
const MULTI_SELECTION_WIDTH = 12;

import Joi from 'joi-browser';

export const paginationSchema = Joi.object().keys({
  activePage: Joi.number().integer().required(),
  totalItemsCount: Joi.number().integer().required(),
  itemsCountPerPage: Joi.number().integer().required(),
  showItemCount: Joi.boolean().allow(null),
});

const TableEmptyMessage = (props)=> (
  <div className='table-not-found'>
    <div className='no-result-text'>{__('No Result', 'Table')}</div>
  </div>
)

class Table extends React.Component {
  constructor(props) {
		super(props);

    this.elemRefsForStickyThead = {}

    this.collectRefs = this.collectRefs.bind(this);
    this.renderUtilSection = this.renderUtilSection.bind(this);
    this._toggleCheckboxTd = this._toggleCheckboxTd.bind(this);
    this._toggleStickyTableHeader	= this._toggleStickyTableHeader.bind(this);

    this.selectAll = this.selectAll.bind(this);
    this.getNonDisabledTrData = this.getNonDisabledTrData.bind(this);
    this.ifAllSelected = this.ifAllSelected.bind(this);

    this.state = {
      stickyTableHeader: false,
      selected: {},
      tableData: [],
    }

  }

	static propTypes = {
		fields: PropTypes.array.isRequired,
		data: PropTypes.array.isRequired,
		getTbodyProps: PropTypes.func,
		useTbodyAsRow: PropTypes.bool,
		useMultiSelection: PropTypes.bool,
    onTrSelectionToggled: PropTypes.func,
    onTrSelect: PropTypes.func,

		settings: PropTypes.array,
		settingThTitle: PropTypes.oneOfType([PropTypes.string, PropTypes.func, PropTypes.object]),
		settingWidth: PropTypes.string,
    oneSettingPerRow: PropTypes.bool,

    dropdownGuideHeight: PropTypes.number,
    dropdownGuideWidth: PropTypes.number,
    tbodyFilter: PropTypes.func,
    trFilter: PropTypes.func,

    getCheckboxTdProps: PropTypes.func,
    getCheckboxDisabled: PropTypes.func,
    getTrProps: PropTypes.func,

    pagination: PropTypes.object,
    usePagination: PropTypes.bool,
    onPageChange: PropTypes.func,

    topUtilSection: PropTypes.object,
    bottomUtilSection: PropTypes.object,

    selectAllInUtilSection: PropTypes.bool,
    hasTopUtilSection: PropTypes.bool,
  }

  componentDidMount() {
    const mainSectionDomElem = global['--classtop-main-section'];

    if (mainSectionDomElem) {
      mainSectionDomElem.addEventListener('scroll', this._toggleStickyTableHeader);
    }

    this.setOffsetTdHeight();
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.pagination) {
      let validateResult = Joi.validate(nextProps.pagination, paginationSchema);

      if (validateResult.error) {
        throw validateResult.error;
      }
    }

    if (nextProps.selected) {
      this.setState({
        selected: nextProps.selected,
      });
    }

  }

  //
  // We're collecting refs to do "sticky thead effect." position: sticky is not very stable
  // and is not receommended for production. These collected refs will be used in
  // this._toggleStickyTableHeader()
  //
  collectRefs(refName) {
    return (ref)=>{
      if (ref) {
        if (refName == 'th') {
          this.thElems.push(ref);
        } else {
          this.elemRefsForStickyThead[refName] = ref;
        }
      }
    }
  }

  setOffsetTdHeight() {
    const {
      thead,
      offsetTd,
    } = this.elemRefsForStickyThead;

    if (!thead || !offsetTd) {
      return;
    }

    let { height } = thead.getBoundingClientRect();

    offsetTd.style.height = height + 'px';
    offsetTd.style.width = '1px';
  }

  _toggleStickyTableHeader() {

    return; // disable the floating <thead> for now

    const mainSectionDom = global['--classtop-main-section'];

    const {
      table,
      thead,
      offsetTd,
    } = this.elemRefsForStickyThead;

    const offsetTop = table.offsetTop;

    if (!offsetTop) {
      return;
    }

    if (!this.state.stickyTableHeader) {
      if (mainSectionDom.scrollTop >= offsetTop) {
        this.thElems.map((th)=>{
          let { width } = th.getBoundingClientRect();
          th.style.width = width + 'px';
        });

        const { y } = mainSectionDom.getBoundingClientRect();

        thead.style.top = y + 'px';

        this.setState({
          stickyTableHeader: true
        });
      }
    } else {
      let { height } = thead.getBoundingClientRect();
      if (mainSectionDom.scrollTop < offsetTop - height) {

        this.setState({
          stickyTableHeader: false
        });
      }
    }
  }

	render() {
    let {
      fetching,
      id,
      data = [],
      className,
      useTbodyAsRow,
      useMultiSelection,
      settings,
      settingThTitle,
      usePagination = USE_PAGINATION,
      pagination,
      topUtilSection,
      bottomUtilSection,
      selectAllInUtilSection = true,
      hasTopUtilSection = false,
    } = this.props;

    let {
      stickyTableHeader,
    } = this.state;

		let tableClassNames = {
			"tr-is-row": !useTbodyAsRow,
			"tbody-is-row": useTbodyAsRow,
    }

    if (usePagination && !pagination) {
      throw new Error('usePagination is true. However props.pagination{} is null.')
    }

    // to collect refs for <th>
    this.thElems = [];

    const displayTableEmptyMessage = data.length === 0;

    const classname = classnames(
      'classtop-table-container',
      {
        'sticky-table-header': stickyTableHeader,
        'fetching': fetching,
        'empty-table': displayTableEmptyMessage,
      })

    // const hasTopUtilSection = typeof topUtilSection !== 'undefined';
    const hasBottomUtilSection = typeof bottomUtilSection !== 'undefined';
    const disableCheckbox = data.length === 0;

    return (
      <div className={classname} ref={this.collectRefs('table')}>
        <div className='table-curtain'></div>
        { fetching && <Loading type='ring' />}

        { hasTopUtilSection && this.renderUtilSection('top', topUtilSection) }

        <table
          id={id}
          className={classnames("classtop-table", className, tableClassNames)} >
          <thead ref={this.collectRefs('thead')} className={classnames({ hasTopUtilSection })}>
            <tr>
              {useMultiSelection && <th style={{width: MULTI_SELECTION_WIDTH}} ref={this.collectRefs('th')}>

                { !selectAllInUtilSection &&
                  <div className={classnames('toggle-all-checkbox', { disableCheckbox })} onClick={this.selectAll} >
                    <label className='control'>
                      <input type='checkbox' checked={this.ifAllSelected()} />
                      <span className='checkmark'></span>
                    </label>
                  </div> }

              </th>}
              {this._getTheads()}
              {(settings && settings.length > 0) && <th ref={this.collectRefs('th')}>{ settingThTitle || "Settings" }</th>}
            </tr>
          </thead>
          {this._getTbodyRows()}
        </table>

        { displayTableEmptyMessage && TableEmptyMessage() }
        { usePagination && this._renderPaginationToolbar()}
      </div>
		)
  }

    renderUtilSection(type, sectionItem) {
      const { useMultiSelection, data = [], selectAllInUtilSection = true } = this.props;

      const disableCheckbox = data.length === 0;

      return (
        <div className={classnames('util-section', type + '-util-section')}>
          { useMultiSelection && selectAllInUtilSection &&
            <div className={classnames('toggle-all-checkbox', { disableCheckbox })} onClick={this.selectAll} >
              <label className='control'>
                <input type='checkbox' checked={this.ifAllSelected()} />
                <span className='checkmark'></span>
              </label>
              
            </div> }
          <div className={classnames('section-content', { useMultiSelection })} >
            {sectionItem}
          </div>
        </div>
      );
    }

    getNonDisabledTrData() {
      let { getCheckboxDisabled, useTbodyAsRow, data = [] } = this.props;
      let newSelected = {};
      data.map((item, idx)=>{
        let trData;
        if (useTbodyAsRow) {
          trData = item[0];
        } else {
          trData = item;
        }

        let checkboxDisabled = false;
        if (typeof getCheckboxDisabled == 'function') {
          checkboxDisabled = getCheckboxDisabled({
            trData,
            trIndex: idx,
            data,
          });
        }

        if (checkboxDisabled != true) {
          newSelected[idx] = trData;
        }
      });

      return newSelected;
    }

    selectAll() {
      let { getCheckboxDisabled, onTrSelectionToggled, useTbodyAsRow, onTrSelect } = this.props;

      let { selected } = this.state;

      let enabledTrData = this.getNonDisabledTrData();

      if (Object.keys(selected).length === Object.keys(enabledTrData).length) {
        this.setState({
          selected: {},
        }, ()=>{
          if (typeof onTrSelectionToggled == "function") {
            onTrSelectionToggled({ selected: [], toggleAll: true });
          }

          if (typeof onTrSelect === 'function') {
            onTrSelect({ selected: [], toggleAll: true });
          }
        });

        return;
      }

      this.setState({
        selected: enabledTrData,
      }, ()=>{
        let selectedArr = [];

        forOwn(this.state.selected, (tr)=>{
          selectedArr.push(tr);
        });

        if (typeof onTrSelectionToggled == "function") {
          onTrSelectionToggled({ selected: selectedArr, toggleAll: true, });
        }

        if (typeof onTrSelect === 'function') {
          onTrSelect({ selected: selectedArr, toggleAll: true, });
        }
      });



    }

    ifAllSelected() {
      const enabledTrData = this.getNonDisabledTrData();
      const { selected } = this.state;
      const dataLength = Object.keys(enabledTrData).length;
      return Object.keys(selected).length === dataLength && dataLength !== 0;
    }

    _renderPaginationToolbar() {
      const {
        onPageChange = function() {
          console.error('warning: props.onPageChange for <Table /> is not defined. A default behaviour is triggered which is this message.');
        },
        onItemsCountPerPageChange = function() {
          console.error('warning: props.onItemsCountPerPageChange for <Table /> is not defined. A default behaviour is triggered which is this message.');
        },
      } = this.props;

      const {
        activePage,
        itemsCountPerPage,
        totalItemsCount,
        pageRangeDisplayed,
        showItemCount = true,
      } = this.props.pagination;
      
      return (
        <div className='table-toolbar'>
          <span>{this._getPaginationInfoText(this.props.pagination)}</span>
          <Pagination
            activePage={activePage}
            itemsCountPerPage={itemsCountPerPage}
            totalItemsCount={totalItemsCount}
            pageRangeDisplayed={pageRangeDisplayed}
            onChange={onPageChange}
          />
          { showItemCount && <select
            className='select-items-count-per-page form-control'
            value={itemsCountPerPage}
            onChange={(e)=>{ onItemsCountPerPageChange(parseInt(e.target.value)); }} >
            <option value='5'>5</option>
            <option value='10'>10</option>
            <option value='25'>25</option>
            <option value='50'>50</option>
          </select>}
        </div>
       );

    }

    _getPaginationInfoText(pagination) {
      let {
        activePage,
        totalItemsCount,
        itemsCountPerPage,
      } = pagination;

      let begin = (activePage - 1) * itemsCountPerPage + 1;
      let end = activePage * itemsCountPerPage;

      if (begin > totalItemsCount) {
        begin = 1;
      }

      if (end > totalItemsCount) {
        end = totalItemsCount;
      }

      if (totalItemsCount == 0) {
        return '';
      }

      return __('Showing {begin} - {end} (total {total})', 'Table', {
        begin,
        end,
        total: totalItemsCount,
      });

      //{activePage} / {Math.ceil(totalItemsCount / itemsCountPerPage)}
    }

		_getTheads() {
			let { fields } = this.props;

			return fields.map((field, idx)=>{
				return (
					<th key={idx} ref={this.collectRefs('th')}>{field.title}</th>
				);
			});
		}

		_getTbodyRows() {
			let { data, fields, getTbodyProps, useTbodyAsRow, tbodyFilter } = this.props;

			// Why are we doing this?
			// Now when useTbodyAsRow != true, which is the most case,
			// we expect data to come simply in a [ {},{}... ] form.
			// Since by default, we iterate an array of tbodies
			// and then trs within each tbody. We will treat that data as a
			// single tbody. So we turn it into:
			// 		[ [ {}, {}... ] ]
			let tableData = useTbodyAsRow != true ? [data] : data;

			return tableData.map((tbodyData, idx)=>{
				let tbodyProps = {}
				if (typeof getTbodyProps == "function") {
					tbodyProps = getTbodyProps({
						tbodyData,
						index: idx,
						data,
						fields,
					});
        }

        if (typeof tbodyFilter === 'function' && !tbodyFilter({ ...this.props, tbodyData, tbodyIndex })) {
          return;
        }

				return (<tbody key={idx} {...tbodyProps}>{this._getTr(tbodyData, idx)}</tbody>);
			});
		}

		_getTr(tbodyData, tbodyIndex) {
			let { oneSettingPerRow, useMultiSelection, settings, trFilter, getTrProps } = this.props;

			return (tbodyData || []).map((trData, idx)=>{
				let settingTd;
				if (Array.isArray(settings) && settings.length > 0) {
					if (oneSettingPerRow && idx > 0) {
						settingTd = <td></td>
					} else {
						settingTd =	<TableSetting {...this.props} trData={trData} trIndex={idx} tbodyIndex={tbodyIndex} />
					}
				}

        if (typeof trFilter === 'function' && !trFilter({ ...this.props, trData, trIndex: idx, tbodyData, tbodyIndex })) {
          return;
        }

        let trProps = {}
        if (typeof getTrProps === 'function') {
          trProps = getTrProps({
            trData,
            trIdx: idx,
            tbodyIndex,
            tbodyData,
          });
        }

				return (
					<tr key={idx} {...trProps}>
						{ useMultiSelection && this._getCheckboxTd(idx, trData, tbodyIndex) }
						{ this._getTd(trData, idx, tbodyData) }
						{ settingTd }
					</tr>);
			});
		}

		_getCheckboxTd(trIdx, trData, tbodyIndex) {
      let { useTbodyAsRow, data = [], getCheckboxProps, getCheckboxTdProps, getCheckboxDisabled } = this.props;

      const params = {
        trIdx, trData, tbodyIndex, data,
      }

      let checkboxProps = {}
      if (typeof getCheckboxProps === 'function') {
        checkboxProps = getCheckboxProps(params);
      }

      let checkboxDisabled = false;
      if (typeof getCheckboxDisabled === 'function') {
        checkboxDisabled = getCheckboxDisabled(params);
      }

      let tdProps = {}
      if (typeof getCheckboxTdProps === 'function') {
        tdProps = getCheckboxTdProps(params);
      }

      if (tdProps.style) {
        tdProps.style = { width: MULTI_SELECTION_WIDTH, ...tdProps.style }
      } else {
        tdProps.style = { width: MULTI_SELECTION_WIDTH }
      }

      if (checkboxDisabled == true) {
        tdProps = { disabled: true, ...tdProps }
      }

      // if we use tbodyAsRow we just need the checkbox in the first tr
			return (
        <td className='checkbox-td' {...tdProps}  >
					{ ((trIdx == 0 && useTbodyAsRow == true) || (useTbodyAsRow != true))
          && <div className='checkbox-container' onClick={(e)=>{
            if (checkboxDisabled != true) {
              this._toggleCheckboxTd(trData, trIdx, tbodyIndex);
            }
          }}>
            <label className='control'>
              <input type="checkbox" checked={this.ifChecked(trIdx, tbodyIndex)} {...checkboxProps} />
              <span className='checkmark'></span>
              </label>
            </div>
          }
				</td>
			);
    }

      ifChecked(trIdx, tbodyIndex) {
				let { useTbodyAsRow } = this.props;
				let idx = useTbodyAsRow ? tbodyIndex : trIdx;
        return typeof this.state.selected[idx] !== "undefined"
      }

			_toggleCheckboxTd(trData, trIdx, tbodyIndex) {
				let { onTrSelectionToggled, onTrSelect, useTbodyAsRow } = this.props;

        let { selected } = this.state;

				// if we use tbodyIndex as a index
				let idx = useTbodyAsRow ? tbodyIndex : trIdx;

        let checked = false;

				if (typeof selected[idx] === 'undefined') {
          checked = true;
          selected[idx] = trData;
        } else {
          delete selected[idx];
        }

        this.setState({
          selected
        }, ()=>{

          let selectedArr = [];

          forOwn(this.state.selected, (tr)=>{
            selectedArr.push(tr);
          });

          let params = {
            checked,
            action: checked ? "checked" : "unchecked",
            selected: selectedArr,
            trIndex: idx,
            trData: trData,
          }

          if (typeof onTrSelectionToggled == "function") {
            onTrSelectionToggled(params);
          }

          if (typeof onTrSelect == "function") {
            onTrSelect(params);
          }
        });


			}

		_getTd(trData, trIndex, tbodyData) {
      let { fields, data } = this.props;

			return fields.map((field, tdIndex)=>{
				let tdData = trData[field.name];

				let params = {
					tdData,
					trData,
					tbodyData,
					data,
					field,
					trIndex,
					tdIndex,
					fields
				};

				let tdProps = {};

				if (typeof field.getTdProps == "function") {
					tdProps = field.getTdProps(params);
				};

				params.tdProps = tdProps;

				if (typeof field.toggleTd == "function" && field.toggleTd(params) == false) {
					return;
				}

				if (typeof field.getTdContent == "function") {
					return (
						<td key={tdIndex} {...tdProps}>
							{field.getTdContent(params)}
						</td>
					);
        } else {
          if (tdData === null || tdData === undefined) {
            tdData = '';
          }

          if (typeof tdData === 'object' || Array.isArray(tdData)) {
            throw new Error('tdData cannot be an object or an array.');
          }
					return (<td key={tdIndex} {...tdProps}>{tdData}</td>);
				}
			});
		}
}

export default Table



// WEBPACK FOOTER //
// ./src/components/UI/Table/Table.js